import * as Sentry from '@sentry/react';

import { UPDATE_STAFFING_STATE } from '../types/staffingTypes';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from 'redux/actions/notifierActions';
import { showAppOverlaySpinner } from './facilitatorSessionActions';
import { InternalApiClient } from 'lib/InternalApiClient';
import {
  isApiFieldValidationError,
  isDuplicateCompanyNameError,
  isDuplicateHubspotCompanyIdError,
  isCompanyHasAssociatedDealsError,
  isVoltageControlRenameError,
  isDuplicateProjectMethodologyTitleError,
  isMethodologyHasAssociatedProjectsError,
} from 'lib/getApiFieldErrors';

/**
 * Authenticated Internal API client for staffing thunks.
 * Tokens are read from Firebase inside {@link InternalApiClient}.
 */
const getStaffingApiClient = () => new InternalApiClient();

// ----------------------------- DEALS ----------------------------- //

/**
 * Sett Deal Insights dialog open or closed
 * @param {boolean} isOpenCreateDealDialog
 * @param {String} dealInsightId
 */
export const openDealInsightsDialog = (isOpenDealInsightsDialog, dealInsightId = null) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { isOpenDealInsightsDialog, dealInsightId },
  };
};

/**
 * Set Create/Edit Deal dialog open or closed
 * @param {boolean} isOpenCreateDealDialog
 * @param {String} dealToUpdateId
 */
export const openCreateDealDialog = (isOpenCreateDealDialog, dealToUpdateId = null) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { isOpenCreateDealDialog, dealToUpdateId },
  };
};

/**
 * Set Create/Edit Deal Project dialog open or closed
 * @param {boolean} isOpenAddProjectToDealDialog
 * @param {String} dealToUpdateId
 */
export const openAddProjectToDealDialog = (
  isOpenAddProjectToDealDialog,
  dealToUpdateId = null,
  projectToUpdateId = null,
) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: {
      isOpenAddProjectToDealDialog,
      dealToUpdateId,
      projectToUpdateId,
    },
  };
};

/**
 * Set Create/Edit Checklist dialog open or closed
 * @param {boolean} isOpenStaffingChecklistModal
 * @param {String} staffingChecklistProjectId
 * @param {String} staffingChecklistCategory
 */
export const openStaffingChecklistModal = (
  isOpenStaffingChecklistModal,
  staffingChecklistProjectId = '',
  staffingChecklistCategory = '',
) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: {
      isOpenStaffingChecklistModal,
      staffingChecklistProjectId,
      staffingChecklistCategory,
    },
  };
};

export const setDealsFilterPayload = filterPayload => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { ...filterPayload },
  };
};

/**
 * Create Deal
 *
 * @param {Object} dealData,
 * @param {Boolean} hasCb
 * @param {Function} successCb
 */
export const createDeal = (dealData, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  apiClient.deals
    .create(dealData)
    .then(({ id }) => {
      if (successCb) {
        successCb(id);
      }
    })
    .catch(err => {
      console.error(err);
      dispatch(enqueueErrorSnackbar('Error creating Deal'));
    });
};

/**
 * Update Deal
 *
 * @param {String} id
 * @param {Object} dealData
 * @param {Function} successCb
 */
export const updateDeal = (id, dealData, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  apiClient.deals
    .update(id, dealData)
    .then(() => {
      if (successCb) {
        successCb(id);
      }
    })
    .catch(err => {
      console.error(err);
      dispatch(enqueueErrorSnackbar('Error updating Deal Info'));
    });
};

/**
 * Delete Deal
 *
 * @param {String} id
 * @param {Object} dealData
 * @param {Function} successCb
 */
export const deleteDeal = (id, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  apiClient.deals
    .delete(id)
    .then(() => {
      if (successCb) successCb();
    })
    .catch(err => {
      console.error(err);
      dispatch(enqueueErrorSnackbar('Error deleting deal'));
    });
};

/**
 * Set Create/Edit Company dialog open or closed.
 *
 * Closing must not clear `companyToUpdateId`. MUI Dialog keeps rendering during its exit
 * animation; if the id is nulled at the same time as `open={false}`, the title/form flash
 * from Edit to Add before the modal is gone. The next open always sets the id (null for
 * New Company) so Add never inherits the last edited company.
 *
 * @param {boolean} isOpenCreateCompanyDialog
 * @param {string|null} [companyToUpdateId] Firestore company id when opening edit; omit/null on open for create
 */
export const openCreateCompanyDialog = (isOpenCreateCompanyDialog, companyToUpdateId) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: isOpenCreateCompanyDialog
      ? { isOpenCreateCompanyDialog, companyToUpdateId: companyToUpdateId ?? null }
      : { isOpenCreateCompanyDialog },
  };
};

const DUPLICATE_COMPANY_SNACKBAR = 'Company already exists. Please enter another Company name';
const DUPLICATE_HUBSPOT_COMPANY_ID_SNACKBAR = 'A company with this HubSpot ID already exists';

/**
 * Create Company
 *
 * @param {string|{ companyName: string, domain?: string, hubspotCompanyId?: string }} companyNameOrData
 *   String form is used by AddDealForm inline create; object form is used by AddCompanyForm.
 * @param {Function} [successCb]
 */
export const createCompany = (companyNameOrData, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  return apiClient.companies
    .create(companyNameOrData)
    .then(({ id }) => {
      if (successCb) {
        successCb(id);
      }
    })
    .catch(err => {
      console.error(err);
      if (isDuplicateHubspotCompanyIdError(err)) {
        dispatch(enqueueErrorSnackbar(DUPLICATE_HUBSPOT_COMPANY_ID_SNACKBAR));
        return;
      }
      dispatch(
        enqueueErrorSnackbar(
          isDuplicateCompanyNameError(err) ? DUPLICATE_COMPANY_SNACKBAR : 'Error creating Company',
        ),
      );
    });
};

/**
 * Update an existing Company (name, domain, etc).
 *
 * @param {string} companyId
 * @param {{ companyName?: string, domain?: string, hubspotCompanyId?: string }} companyData
 * @param {Function} [successCb]
 */
export const updateCompany = (companyId, companyData, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  return apiClient.companies
    .update(companyId, companyData)
    .then(() => {
      if (successCb) {
        successCb(companyId);
      }
    })
    .catch(err => {
      console.error(err);
      if (isVoltageControlRenameError(err)) {
        dispatch(enqueueErrorSnackbar('Voltage Control cannot be renamed'));
        return;
      }
      if (isDuplicateHubspotCompanyIdError(err)) {
        dispatch(enqueueErrorSnackbar(DUPLICATE_HUBSPOT_COMPANY_ID_SNACKBAR));
        return;
      }
      dispatch(
        enqueueErrorSnackbar(
          isDuplicateCompanyNameError(err) ? DUPLICATE_COMPANY_SNACKBAR : 'Error updating Company',
        ),
      );
    });
};

const COMPANY_HAS_DEALS_DELETE_SNACKBAR =
  'A company cannot be deleted while it has associated deals. Delete the deals first and try again.';

/**
 * Delete a Company. The API rejects companies that still have deals (409).
 *
 * @param {string} companyId
 */
export const deleteCompany = companyId => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  return apiClient.companies
    .delete(companyId)
    .then(() => {
      dispatch(enqueueSuccessSnackbar('Company deleted'));
    })
    .catch(err => {
      console.error(err);
      dispatch(
        enqueueErrorSnackbar(
          isCompanyHasAssociatedDealsError(err)
            ? COMPANY_HAS_DEALS_DELETE_SNACKBAR
            : 'Error deleting Company',
        ),
      );
    });
};

// ----------------------------- END OF DEALS ----------------------------- //

// ----------------------------- CONTRACTORS ----------------------------- //

/**
 * Sett Project Insights dialog open or closed
 * @param {boolean} isOpenProjectInsightsDialog
 * @param {String} projectInsightId
 */
export const openProjectInsightsDialog = (isOpenProjectInsightsDialog, projectInsightId = null) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { isOpenProjectInsightsDialog, projectInsightId },
  };
};

/**
 * Set Create/Edit Contractor dialog open or closed
 * @param {boolean} isOpenCreateContractorDialog
 * @param {String} contractorToUpdateId
 */
export const openCreateContractorDialog = (
  isOpenCreateContractorDialog,
  contractorToUpdateId = null,
) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { isOpenCreateContractorDialog, contractorToUpdateId },
  };
};

/* Set Create/edit blocked time dialog open or closed
 * @param {boolean} isOpenCreateBlockedTimeDialog
 */
export const openCreateBlockedTimeDialog = (
  isOpenCreateBlockedTimeDialog,
  blockedTimeToUpdateId = null,
) => {
  console.log('openCreateBlockedTimeDialog: blockedTimeToUpdateId', blockedTimeToUpdateId);
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { isOpenCreateBlockedTimeDialog, blockedTimeToUpdateId },
  };
};

/**
 * Create Staff Blocked Time
 *
 * @param {Object} blockedTimeData,
 * @param {Boolean} hasCb
 * @param {Function} successCb
 */
export const createStaffBlockedTime =
  (blockedTimeData, hasCb, successCb) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    console.log('createStaffBlockedTime: blockedTimeData', blockedTimeData);
    apiClient.staffBlockedTimes
      .create(blockedTimeData)
      .then(() => {
        if (hasCb) {
          successCb();
        }
      })
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error creating blocked time'));
      });
  };

/**
 * Update Staff Blocked Time
 *
 * @param {String} blockedTimeId
 * @param {Object} blockedTimeData
 * @param {Boolean} hasCb
 * @param {Function} successCb
 */
export const updateStaffBlockedTime =
  (blockedTimeId, blockedTimeData, hasCb, successCb) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    console.log('updateStaffBlockedTime: blockedTimeId', blockedTimeId);
    console.log('updateStaffBlockedTime: blockedTimeData', blockedTimeData);
    apiClient.staffBlockedTimes
      .update(blockedTimeId, blockedTimeData)
      .then(() => {
        if (hasCb) {
          successCb();
        }
      })
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error updating blocked time'));
      });
  };

/**
 * Delete StaffBlockedTime
 *
 * @param {String} blockedTimeId
 * @param {Function} successCb
 */
export const deleteStaffBlockedTime = (blockedTimeId, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  apiClient.staffBlockedTimes
    .delete(blockedTimeId)
    .then(() => {
      if (successCb) successCb();
    })
    .catch(err => {
      console.error(err);
      dispatch(enqueueErrorSnackbar('Error deleting blocked time'));
    });
};

/**
 * Set Add Contractor To Job Dialog open or closed
 * @param {boolean} isOpenAddToJobDialog
 * @param {String} contractorToUpdateId
 */
export const openAddContractorToJobDialog = (isOpenAddToJobDialog, contractorToUpdateId) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { isOpenAddToJobDialog, contractorToUpdateId },
  };
};

/**
 * Set Contractor Filter Params Payload
 * @param {Object} filterPayload
 */
export const setContractorFilterPayload = filterPayload => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { ...filterPayload },
  };
};

/**
 * Set Schedules Filter Params Payload
 * @param {Object} filterPayload
 */
export const setSchedulesFilterPayload = filterPayload => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { ...filterPayload },
  };
};

/**
 * Create Contractor User
 *
 * @param {Object} contractorData,
 * @param {Boolean} hasCb
 * @param {Function} successCb
 */
export const createContractorUser = (contractorData, hasCb, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  return apiClient.contractors
    .create(contractorData)
    .then(() => {
      if (hasCb) {
        successCb();
      }
    })
    .catch(err => {
      if (isApiFieldValidationError(err)) {
        throw err;
      }
      console.error(err);
      dispatch(enqueueErrorSnackbar('Error create contractor user'));
      throw err;
    });
};

/**
 * Update Contractor User
 *
 * @param {String} userId
 * @param {Object} contractorData
 * @param {Boolean} hasCb
 * @param {Function} successCb
 */
export const updateContractorUser =
  (userId, contractorData, hasCb, successCb) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    return apiClient.contractors
      .update(userId, contractorData)
      .then(() => {
        if (hasCb) {
          successCb();
        }
      })
      .catch(err => {
        if (isApiFieldValidationError(err)) {
          throw err;
        }
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error updating Staff Info'));
        throw err;
      });
  };

/**
 * Delete Contractor User
 *
 * @param {String} contractorId
 * @param {Function} successCb
 */
export const deleteContractorUser = (contractorId, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  apiClient.contractors
    .delete(contractorId)
    .then(() => successCb && successCb())
    .catch(err => {
      console.error(err);
      dispatch(enqueueErrorSnackbar('Error deleting Staff User'));
    });
};

// ----------------------------- PROJECTS ----------------------------- //

/**
 * Set Add Job to Project dialog open or closed
 * @param {boolean} isOpenAddJobToProjectDialog
 * @param {String} projectToUpdateId
 * @param {String} jobToUpdateId
 */
export const openAddJobToProjectDialog = (
  isOpenAddJobToProjectDialog,
  projectToUpdateId = null,
  jobToUpdateId = null,
) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { isOpenAddJobToProjectDialog, projectToUpdateId, jobToUpdateId },
  };
};

/**
 * Set Add Job to Project dialog open or closed
 * @param {boolean} isOpenAddJobToDealProjectDialog
 * @param {String} dealProjectToUpdateId
 * @param {String} jobToUpdateId
 */
export const openAddJobToDealProjectDialog = (
  isOpenAddJobToDealProjectDialog,
  dealProjectToUpdateId = null,
  jobToUpdateId = null,
) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: {
      isOpenAddJobToDealProjectDialog,
      dealProjectToUpdateId,
      jobToUpdateId,
    },
  };
};

/**
 * Set Add Volunteer Role to Project dialog open or closed
 * @param {boolean} isOpenAddVolunteerRoleToProjectDialog
 * @param {String} dealProjectToUpdateId
 * @param {String} volunteerRoleToUpdateId
 */
export const openAddVolunteerRoleToProjectDialog = (
  isOpenAddVolunteerRoleToProjectDialog,
  dealProjectToUpdateId = null,
  volunteerRoleToUpdateId = null,
) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: {
      isOpenAddVolunteerRoleToProjectDialog,
      dealProjectToUpdateId,
      volunteerRoleToUpdateId,
    },
  };
};

/**
 * Set Clone Job open/closed
 * @param {boolean} isOpenCloneJobDialog
 * @param {String} projectToUpdateId
 * @param {String} jobToCloneId
 */
export const openCloneJobDialog = (
  isOpenCloneJobDialog,
  projectToUpdateId = null,
  jobToCloneId = null,
) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: {
      isOpenCloneJobDialog,
      projectToUpdateId,
      jobToUpdateId: jobToCloneId,
    },
  };
};

/**
 * Set Manage Contractors Dialog open or closed
 * @param {boolean} isOpenManageContractorDialog
 * @param {String} projectToUpdateId
 * @param {String} jobToUpdateId
 */
export const openManageContractorsDialog = (
  isOpenManageContractorDialog,
  projectToUpdateId = null,
  jobToUpdateId = null,
) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { isOpenManageContractorDialog, projectToUpdateId, jobToUpdateId },
  };
};

/**
 * Set Manage Project Methodologies dialog open or closed.
 *
 * @param {boolean} isOpenManageProjectMethodologiesDialog
 */
export const openManageProjectMethodologiesDialog = isOpenManageProjectMethodologiesDialog => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { isOpenManageProjectMethodologiesDialog },
  };
};

const DUPLICATE_METHODOLOGY_SNACKBAR =
  'Methodology already exists. Please enter another Methodology name';
const METHODOLOGY_HAS_PROJECTS_DELETE_SNACKBAR =
  'This methodology cannot be deleted because it is used by one or more projects. Remove it from those projects first and try again.';

/**
 * Create Project Methodology
 *
 * @param {string} title Methodology display name.
 * @param {Function} [successCb] Called with the new methodology id on success.
 * @returns {Promise<boolean>} True when create succeeded so callers can stop a spinner.
 */
export const createProjectMethodology = (title, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  return apiClient.projectMethodologies
    .create({ title })
    .then(methodology => {
      if (successCb) {
        successCb(methodology.id);
      }
      return true;
    })
    .catch(err => {
      console.error(err);
      dispatch(
        enqueueErrorSnackbar(
          isDuplicateProjectMethodologyTitleError(err)
            ? DUPLICATE_METHODOLOGY_SNACKBAR
            : 'Error creating Project Methodology',
        ),
      );
      return false;
    });
};

/**
 * Rename an existing Project Methodology.
 *
 * Returns false on 409/other errors so the manage modal can stay in edit mode instead of
 * snapping back to text as if save succeeded.
 *
 * @param {string} methodologyId
 * @param {{ title: string }} methodologyData
 * @param {Function} [successCb]
 * @returns {Promise<boolean>}
 */
export const updateProjectMethodology =
  (methodologyId, methodologyData, successCb) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    return apiClient.projectMethodologies
      .update(methodologyId, methodologyData)
      .then(() => {
        if (successCb) {
          successCb(methodologyId);
        }
        return true;
      })
      .catch(err => {
        console.error(err);
        dispatch(
          enqueueErrorSnackbar(
            isDuplicateProjectMethodologyTitleError(err)
              ? DUPLICATE_METHODOLOGY_SNACKBAR
              : 'Error updating Project Methodology',
          ),
        );
        return false;
      });
  };

/**
 * Delete a Project Methodology. The API rejects methodologies that still have projects (409).
 *
 * @param {string} methodologyId
 * @returns {Promise<boolean>}
 */
export const deleteProjectMethodology = methodologyId => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  return apiClient.projectMethodologies
    .delete(methodologyId)
    .then(() => {
      dispatch(enqueueSuccessSnackbar('Methodology deleted'));
      return true;
    })
    .catch(err => {
      console.error(err);
      dispatch(
        enqueueErrorSnackbar(
          isMethodologyHasAssociatedProjectsError(err)
            ? METHODOLOGY_HAS_PROJECTS_DELETE_SNACKBAR
            : 'Error deleting Project Methodology',
        ),
      );
      return false;
    });
};

/**
 * Add Checklist Items to Project
 *
 * @param {String} projectId
 * @param {String} checklistId
 * @param {Object} checklistData,
 * @param {Function} successCb
 */
export const addChecklistToProject =
  (projectId, checklistId, checklistData, successCb) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    apiClient.projects.checklists
      .add(projectId, checklistId, checklistData)
      .then(() => {
        if (successCb) {
          successCb();
        }
      })
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error adding/updating Checklist to project'));
      });
  };

/**
 * Create Project
 *
 * @param {Object} projectData,
 * @param {Boolean} hasCb
 * @param {Function} successCb
 */
export const createProject = (projectData, hasCb, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  // Callers own UX (form pins field errors / StatusErrorDialog shows the API message). Do not
  // flatten failures into a hardcoded snackbar here — that discards the response body.
  return apiClient.projects
    .create(projectData)
    .then(({ id }) => {
      if (hasCb) {
        successCb(id);
      }
    })
    .catch(err => {
      console.error(err);
      throw err;
    });
};

/**
 * Update Project
 *
 * @param {String} id
 * @param {Object} projectData
 * @param {Function} successCb
 */
export const updateProject =
  (id, projectData, successCb, previousDealId) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    // Same contract as createProject: return the promise and rethrow so Formik can pin field
    // errors and ProjectDetail can open StatusErrorDialog. Fire-and-forget status buttons must
    // .catch() themselves (see persistProjectUpdate in ProjectDetail).
    return apiClient.projects
      .update(id, projectData, previousDealId)
      .then(() => successCb && successCb(id))
      .catch(err => {
        console.error(err);
        throw err;
      });
  };

/**
 * Delete Project Data
 *
 * @param {String} projectId
 * @param {Function} successCb
 * @param {Function=} errorCb
 */
export const deleteProject = (projectId, dealId, successCb, errorCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  apiClient.projects
    .delete(projectId, dealId)
    .then(() => successCb && successCb())
    .catch(err => {
      console.error(err);
      dispatch(enqueueErrorSnackbar('Error Deleting Project'));
      errorCb && errorCb();
    });
};

/**
 * Add Jobs to Project
 *
 * @param {String} projectId
 * @param {String} jobId
 * @param {Object} jobData,
 * @param {Function} successCb
 */
export const addJobToProject = (projectId, jobId, jobData, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  apiClient.projects.jobs
    .create(projectId, jobId, jobData)
    .then(() => {
      if (successCb) {
        successCb();
      }
    })
    .catch(err => {
      console.error(err);
      dispatch(enqueueErrorSnackbar('Error adding Job to project'));
    });
};

/**
 * Add Volunteer Role to Project
 *
 * @param {String} projectId
 * @param {String} volunteerRoleId
 * @param {Object} volunteerRoleData
 * @param {Function} successCb
 */
export const addVolunteerRoleToProject =
  (projectId, volunteerRoleId, volunteerRoleData, successCb) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    apiClient.projects.volunteerRoles
      .upsert(projectId, volunteerRoleId, volunteerRoleData)
      .then(() => {
        if (successCb) {
          successCb();
        }
      })
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error adding or updating Volunteer Role'));
      });
  };

/**
 * Delete Job Data
 *
 * @param {String} projectId
 * @param {String} jobId
 * @param {Function} successCb
 */
export const deleteJob = (projectId, jobId, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  apiClient.projects.jobs
    .delete(projectId, jobId)
    .then(() => {
      if (successCb) {
        successCb();
      }
    })
    .catch(err => {
      console.error(err);
      dispatch(enqueueErrorSnackbar('Error deleting Job from project'));
    });
};

/**
 * Delete Volunteer Role
 *
 * @param {String} projectId
 * @param {String} volunteerRoleId
 * @param {Function} successCb
 */
export const deleteVolunteerRole =
  (projectId, volunteerRoleId, successCb) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    apiClient.projects.volunteerRoles
      .delete(projectId, volunteerRoleId)
      .then(() => {
        if (successCb) {
          successCb();
        }
      })
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error deleting Volunteer Role from project'));
      });
  };

/**
 * Edit Volunteer
 *
 * @param {String} projectId
 * @param {String} volunteerId
 * @param {Object} volunteerData
 * @param {Function} successCb
 */
export const updateVolunteer =
  (projectId, volunteerId, volunteerData, successCb) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    apiClient.projects.volunteers
      .upsert(projectId, volunteerId, volunteerData)
      .then(() => {
        if (successCb) {
          successCb();
        }
      })
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error editing Volunteer'));
      });
  };

/**
 * Delete Volunteer Data
 *
 * @param {String} projectId
 * @param {String} volunteerId
 * @param {Function} successCb
 */
export const deleteVolunteer = (projectId, volunteerId, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  apiClient.projects.volunteers
    .delete(projectId, volunteerId)
    .then(() => {
      if (successCb) {
        successCb();
      }
    })
    .catch(err => {
      console.error(err);
      dispatch(enqueueErrorSnackbar('Error deleting Volunteer from project'));
    });
};

/**
 * Add Contactor to Job
 *
 * @param {String} projectId
 * @param {String} jobId
 * @param {String} contractorId
 * @param {Function} successCb
 */
export const addContractorToJob =
  (projectId, jobId, contractorId, successCb) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();
    const { email } = getState().firebase.auth;

    apiClient.projects.jobs
      .createInvite(projectId, jobId, contractorId, email)
      .then(() => {
        if (successCb) {
          successCb();
        }
      })
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error adding Staff to Job'));
      });
  };

/**
 * Delete/Unassign Contactor from Job
 *
 * @param {String} projectId
 * @param {String} jobId
 * @param {String} contractorId
 * @param {Function} successCb
 */
export const removeContractorFromJob =
  (projectId, jobId, contractorId, successCb) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    apiClient.projects.jobs
      .removeAssignee(projectId, jobId, contractorId)
      .then(() => {
        if (successCb) {
          successCb();
        }
      })
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error removing Staff from Project'));
      });
  };

/**
 * Update Assignee Info on Job in Project
 *
 * @param {String} projectId
 * @param {String} jobId
 * @param {String} contractorId
 * @param {Object} values
 * @param {Function} successCb
 */
export const updateAssigneeInJob =
  (projectId, jobId, contractorId, values, successCb) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    apiClient.projects.jobs
      .updateAssignee(projectId, jobId, contractorId, values)
      .then(() => {
        if (successCb) {
          successCb();
        }
      })
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error adding Staff to Job'));
      });
  };

/**
 * Resend invite to contractor for a job by creating a new jobInvites document
 *
 * @param {String} projectId
 * @param {String} jobId
 * @param {String} contractorId
 * @param {Function} successCb
 */
export const resendInvite = (projectId, jobId, contractorId, successCb) => (dispatch, getState) => {
  const apiClient = getStaffingApiClient();

  apiClient.projects.jobs
    .resendInvite(projectId, jobId, contractorId)
    .then(() => {
      if (successCb) {
        successCb();
      }
    })
    .catch(err => {
      console.error(err);
      dispatch(enqueueErrorSnackbar('Error adding Staff to Job'));
    });
};

/**
 * Calls a cloud function to re-sync the calendar entry from the project data
 * @param {string} projectId
 * @param {Function} successCb
 * @param {Function=} errorCb
 */
export const reSyncCalendarEntry =
  (projectId, successCb, errorCb) => async (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    apiClient.projects
      .reSyncCalendar(projectId)
      .then(() => successCb())
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error Re-Syncing calendar'));
        if (errorCb) errorCb();
      });
  };

/**
 * Calls a cloud function to regenerate the run of show
 * @param {string} projectId
 * @param {Function} successCb
 * @param {Function=} errorCb
 */
export const callRegenerateRunOfShow =
  (projectId, successCb, errorCb) => async (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    apiClient.projects
      .regenerateRunOfShow(projectId)
      .then(() => successCb())
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error Regenerating Run of Show'));
        if (errorCb) errorCb();
      });
  };

/**
 * Calls a cloud function to re-sync the attendees list from the project data
 * @param {string} projectId
 * @param {Function} successCb
 * @param {Function=} errorCb
 */
export const reSyncAttendeesListFromSheet =
  (projectId, successCb, errorCb) => async (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    apiClient.projects
      .syncAttendeeList(projectId)
      .then(() => successCb())
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error Syncing Attendees List'));
        if (errorCb) errorCb();
      });
  };

/**
 * Calls the CR API to update the Circle user info for a contractor
 * @param {string} contractorId
 * @param {Function} successCb
 * @param {Function=} errorCb
 */
export const updateCircleUserForContractor =
  (contractorId, successCb, errorCb) =>
  async (dispatch, getState, { getFirebase }) => {
    const apiClient = getStaffingApiClient();

    apiClient.contractors
      .syncCircleUser(contractorId)
      .then(successCb)
      .catch(err => {
        if (errorCb) errorCb(err);
      });
  };

/**
 * Calls a cloud function to upload the attendees list to HubSpot
 * @param {string} projectId
 * @param {boolean} dryRun
 * @param {number} skip
 * @param {number} max
 * @param {Function} successCb
 * @param {Function=} errorCb
 */
export const callUploadAttendeesToHS =
  (projectId, dryRun, skip, max, successCb, errorCb) => async (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    apiClient.projects
      .uploadAttendeesToHs(projectId, { dryRun, skip, max })
      .then(result => {
        successCb(result.sessionId);
      })
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error uploading Attendees to HubSpot'));
        if (errorCb) errorCb();
      });
  };

/**
 * Calls a cloud function to re-sync the project messaging config
 * @param {string} projectId
 * @param {Function} successCb
 * @param {Function=} errorCb
 */
export const reSyncAttendeeMessagingConfig =
  (projectId, successCb, errorCb) => async (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    apiClient.projects
      .refreshMessagingConfig(projectId)
      .then(() => successCb())
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error syncing messaging config'));
        if (errorCb) errorCb();
      });
  };

/**
 * Calls a cloud function to override the project messaging config and
 * another to re-sync the project messaging config to the project record
 * @param {string} projectId
 * @param {Function} successCb
 * @param {Function=} errorCb
 */
export const overrideDefaultAttendeeMessagingConfig =
  (projectId, successCb, errorCb) => async (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    apiClient.projects
      .overrideMessagingSchedule(projectId)
      .then(() => apiClient.projects.refreshMessagingConfig(projectId))
      .then(() => successCb())
      .catch(err => {
        console.error(err);
        dispatch(enqueueErrorSnackbar('Error overriding or syncing messaging config'));
        if (errorCb) errorCb();
      });
  };

// ----------------------------- CONTRACTOR DASHBOARD ----------------------------- //

/**
 * Set ProjectRoleAction dialog open or closed
 * @param {boolean} isOpenProjectRoleActionDialog
 * @param {String} projectRoleActionDialogType
 * @param {String} jobToUpdateId
 * @param {String} projectId
 */
export const openProjectRoleActionDialog = (
  isOpenProjectRoleActionDialog,
  projectRoleActionDialogType = '',
  jobToUpdateId = null,
  projectId = null,
) => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: {
      isOpenProjectRoleActionDialog,
      projectRoleActionDialogType,
      jobToUpdateId,
      projectId,
    },
  };
};

/**
 * Set ProjectRoleActionFeedback dialog open or closed
 * @param {boolean} isOpenProjectRoleActionFeedbackDialog
 */
export const openProjectRoleActionFeedbackDialog = isOpenProjectRoleActionFeedbackDialog => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { isOpenProjectRoleActionFeedbackDialog },
  };
};

/**
 * Sends Contract Reminder
 * @param {String} projectId
 * @param {String} jobId
 * @param {String} contractorEmail
 * @param {String} helloSignRequestId
 * @param {Boolean} isContractor
 */
export const sendContractReminder =
  (projectId, jobId, contractorEmail, helloSignRequestId, isContractor = false) =>
  (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    dispatch(showAppOverlaySpinner(true));

    apiClient.projects.jobs
      .resendStaffingContract(projectId, jobId, {
        email: contractorEmail,
        signatureRequestId: helloSignRequestId,
      })
      .then(() => {
        dispatch(showAppOverlaySpinner(false));

        if (isContractor) {
          dispatch(enqueueSuccessSnackbar(`Contract resent to your email`));
        } else {
          dispatch(enqueueSuccessSnackbar(`Contract resent to ${contractorEmail}`));
        }
      })
      .catch(err => {
        Sentry.captureException(err);
        dispatch(showAppOverlaySpinner(false));
        dispatch(enqueueErrorSnackbar('Error resending Contract'));
      });
  };

/**
 * Creates and sends a contract for a Contractor
 * @param {String} projectId
 * @param {String} contractorId
 * @param {String} jobId
 * @param {String} contractorEmail
 */
export const createAndSendContract =
  (projectId, contractorId, jobId, contractorEmail) => (dispatch, getState) => {
    const apiClient = getStaffingApiClient();

    dispatch(showAppOverlaySpinner(true));

    apiClient.projects.jobs
      .createAndSendStaffingContract(projectId, jobId, contractorId)
      .then(() => {
        dispatch(showAppOverlaySpinner(false));
        dispatch(enqueueSuccessSnackbar(`Contract sent to ${contractorEmail}`));
      })
      .catch(err => {
        Sentry.captureException(err);
        dispatch(showAppOverlaySpinner(false));
        dispatch(enqueueErrorSnackbar('Error creating and sending contract'));
      });
  };

// ----------------------------- SCHEDULES PAGE ----------------------------- //
export const changeSelectedStartDate = startDate => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { selectedScheduleStartDate: startDate },
  };
};

export const changeSelectedEndDate = endDate => {
  return {
    type: UPDATE_STAFFING_STATE,
    payload: { selectedScheduleEndDate: endDate },
  };
};
